import React, { ReactNode } from 'react';
import { PageLayout, PageLayoutProps } from './PageLayout';

interface DashboardLayoutProps extends Omit<PageLayoutProps, 'children'> {
  /** Thẻ KPI hiển thị ở trên cùng */
  kpis?: ReactNode;
  /** Nội dung chính (Biểu đồ, Bảng dữ liệu quan trọng) - Thường chiếm 2/3 chiều rộng */
  children?: ReactNode;
  /** Nội dung bên cạnh (Hoạt động gần đây, Thông tin nhanh) - Thường chiếm 1/3 chiều rộng */
  sidebar?: ReactNode;
  /** Các nút thao tác nhanh hiển thị ở dưới cùng hoặc vị trí phù hợp */
  quickActions?: ReactNode;
  /** Các cảnh báo hoặc thông báo quan trọng ở đầu dashboard */
  announcements?: ReactNode;
  /** Layout sidebar: 'right' (mặc định) hoặc 'left' */
  sidebarPosition?: 'left' | 'right';
  /** Ẩn sidebar trên mobile */
  hideSidebarOnMobile?: boolean;
  /** Số cột KPI trên desktop (mặc định là 4) */
  kpiColumns?: number;
  /** Số cột KPI trên mobile (mặc định là 2) */
  mobileKpiColumns?: number;
}

/**
 * DashboardLayout - Layout chuyên dụng cho các trang Dashboard.
 * Tự động sắp xếp các thành phần theo lưới (Grid) chuẩn Landscape.
 */
export function DashboardLayout({
  kpis,
  children,
  sidebar,
  quickActions,
  announcements,
  sidebarPosition = 'right',
  hideSidebarOnMobile = false,
  kpiColumns = 4,
  mobileKpiColumns = 2,
  ...pageProps
}: DashboardLayoutProps) {

  const mainOrder = sidebarPosition === 'right' ? 'order-1' : 'order-2';
  const sidebarOrder = sidebarPosition === 'right' ? 'order-2' : 'order-1';

  return (
    <PageLayout {...pageProps}>
      <div className="space-y-6">
        {/* 1. Phần Thông báo/Banner quan trọng */}
        {announcements && (
          <div className="animate-in fade-in slide-in-from-top-2 duration-500">
            {announcements}
          </div>
        )}

        {/* 2. Phần chỉ số KPI chính (Row) */}
        {kpis && (
          <div 
            className={`grid gap-4 ${
              mobileKpiColumns === 3 ? 'grid-cols-3' : 
              mobileKpiColumns === 1 ? 'grid-cols-1' : 'grid-cols-2'
            }`}
            style={{ 
              gridTemplateColumns: window.innerWidth >= 768 ? `repeat(${kpiColumns}, minmax(0, 1fr))` : undefined 
            }}
          >
            {kpis}
          </div>
        )}

        {/* 3. Phần thân Dashboard (Grid 2-1) */}
        {(children || sidebar) && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Column */}
            <div className={`${sidebar ? 'lg:col-span-2' : 'lg:col-span-3'} space-y-6 ${mainOrder}`}>
              {children}
            </div>

            {/* Sidebar Column */}
            {sidebar && (
              <div className={`space-y-6 ${sidebarOrder} ${hideSidebarOnMobile ? 'hidden lg:block' : ''}`}>
                {sidebar}
              </div>
            )}
          </div>
        )}

        {/* 4. Thao tác nhanh (Bottom Section) */}
        {quickActions && (
          <div className="pt-2">
            <div className="flex items-center gap-3 mb-3">
              <div className="flex-1 h-px bg-slate-100" />
              <span className="text-xs text-slate-400 tracking-widest whitespace-nowrap">Thao tác nhanh</span>
              <div className="flex-1 h-px bg-slate-100" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {quickActions}
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
}
